import { ReactNode, useEffect, useRef, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import {
  alpha,
  Avatar,
  Box,
  Button,
  Divider,
  Hidden,
  lighten,
  MenuItem,
  MenuList,
  Popover,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';

import { styled, useTheme } from '@mui/material/styles';
import ExpandMoreTwoToneIcon from '@mui/icons-material/ExpandMoreTwoTone';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { BaseAPI } from 'src/providers/base-api';
import { UsersInterface } from 'src/models/CommonInterface';
import { useAuth } from 'src/hooks/useAuth';
import { isSSOCookieSession } from 'src/reducers/authSlice';
import { useCodeTables } from 'src/hooks/useCodeTables';

const UserBoxButton = styled(Button)(
  ({ theme }) => `
        padding: ${theme.spacing(0.5, 1)};
        border-radius: 8px;
        text-transform: none;
        &:hover, &[aria-expanded='true'] {
          background: ${alpha(theme.palette.primary.main, 0.08)};
        }
        `
);

const UserBoxText = styled(Box)(
  ({ theme }) => `
  text-align: left;
  padding-left: ${theme.spacing(1)};
  `
);

const UserBoxLabel = styled(Typography)(
  ({ theme }) => `
  font-weight: ${theme.typography.fontWeightBold};
  color: ${theme.palette.secondary.main};
  display: block;
  text-transform: capitalize
  `
);

const UserBoxDescription = styled(Typography)(
  ({ theme }) => `
  color: ${lighten(theme.palette.secondary.main, 0.5)}
  `
);

const IconTile = ({ tint, children }: { tint: string; children: ReactNode }) => (
  <Box sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: tint, color: 'common.black', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
    {children}
  </Box>
);

function HeaderUserbox() {
  // Use the useAuth hook for user data from Redux
  const { user, isAuthenticated, fullName, firstName, lastName, logout } = useAuth();
  const { getNameById } = useCodeTables();

  const navigate = useNavigate();
  const theme = useTheme();
  const { pathname } = useLocation();
  const ref = useRef<any>(null);
  const [isOpen, setOpen] = useState<boolean>(false);
  const [imgSrc, setImgSrc] = useState<string>('/static/images/avatars/user.png');
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState<boolean>(false);

  // Derive profile picture URL directly from the public streaming endpoint.
  // No async fetch needed — URL is stable (nanoID-based) and browser-cacheable.
  useEffect(() => {
    const profilePictureId = user?.employeeId?.profilePicture;
    if (profilePictureId) {
      const url = BaseAPI.buildProfilePictureURL(profilePictureId);
      if (url) {
        setImgSrc(url);
      }
    }
  }, [user?.employeeId?.profilePicture]);

  // Check for session issues - if authenticated in Redux but user data is missing
  useEffect(() => {
    const checkSession = async () => {
      // If we're supposed to be authenticated but have no user data, there might be an issue
      if (!user && sessionStorage.getItem('LoginInfo')) {
        try {
          const response = await BaseAPI.validateToken();
          if (!response || !response.data) {
            setSessionExpiredOpen(true);
          }
        } catch (error) {
          setSessionExpiredOpen(true);
        }
      }
    };

    // Small delay to allow Redux hydration to complete
    const timeoutId = setTimeout(checkSession, 1000);
    return () => clearTimeout(timeoutId);
  }, [user]);

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleLogout = async () => {
    // Capture BEFORE clearing state below — this is the only signal we need
    // (no access_token in UserDetail means the session was a crelo-platform
    // SSO cookie-only session; see isSSOCookieSession doc comment). Normal
    // bearer-login users are completely unaffected by this branch.
    const wasSSOSession = isSSOCookieSession(user);

    try {
      await BaseAPI.logout('/auth/logout');
    } catch (error) {
      console.error('Logout API error:', error);
    } finally {
      // Always clear session and redirect, even if API fails
      sessionStorage.clear();
      document.cookie = 'auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Strict';

      if (wasSSOSession) {
        // Ends the local HRIS session (clears hris_session cookie) AND
        // redirects the browser to the crelo-platform's RP-initiated
        // logout, so the platform session is revoked too — otherwise the
        // next visit would silently re-authenticate via the still-live
        // platform session. See backend-go/internal/modules/auth/sso/service.go
        // LogoutHandler. Flag-gated server-side: if CRELO_SSO_ENABLED is
        // off, /sso/logout 404s, but that can't happen here since a
        // cookie-only session could only have been established while the
        // flag was on.
        // Absolute backend URL — /sso/logout lives on the API origin
        // (REACT_APP_API_URL), NOT the SPA origin. A relative '/sso/logout'
        // would resolve to the frontend host and just reload the SPA shell.
        window.location.assign(BaseAPI.buildAPIURL('/sso/logout'));
      } else {
        window.location.href = '/login';
      }
    }
  };

  const handleSessionExpiredClose = () => {
    setSessionExpiredOpen(false);
    handleLogout();
  };

  const handleSignInNewTab = () => {
    window.open('/login', '_blank');
    setSessionExpiredOpen(false);
  };

  // Display name - use data from useAuth hook
  const displayFirstName = firstName || '';
  const displayLastName = lastName || '';
  const displayFullName = fullName || 'User';

  // Get party type name from code tables using user.partyType ID
  const displayPartyType = user?.partyType ? getNameById('user_ref', user.partyType) : '';

  // Always render something - even if user data is unavailable
  const hasUserData = displayFirstName || displayLastName;

  const initial = (displayFirstName || displayFullName || 'U').charAt(0).toUpperCase();
  // Soft lavender avatar with an ink initial (shown whenever there is no picture)
  const avatarSx = (size: number, radius: string | number) => ({
    width: size,
    height: size,
    borderRadius: radius,
    bgcolor: theme.colors.pastel.lilac,
    color: theme.palette.common.black,
    fontWeight: 700,
    fontSize: size * 0.4
  });
  const menuItemSx = {
    minHeight: 48,
    borderRadius: '8px',
    gap: 1.5,
    px: 1.25,
    '&.Mui-selected, &:hover, &.Mui-focusVisible': { bgcolor: alpha(theme.palette.primary.main, 0.08) }
  };
  const goTo = (to: string) => {
    handleClose();
    navigate(to);
  };

  return (
    <>
      {/* Session Expired Dialog */}
      <Dialog open={sessionExpiredOpen} onClose={() => { }} disableEscapeKeyDown aria-labelledby="session-expired-dialog-title">
        <DialogTitle id="session-expired-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ErrorOutlineIcon color="warning" />
          Session Expired
        </DialogTitle>
        <DialogContent>
          <DialogContentText>Your session has expired or is invalid. Please sign in again to continue.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSignInNewTab} color="primary">
            Sign in (New Tab)
          </Button>
          <Button onClick={handleSessionExpiredClose} color="primary" variant="contained">
            Sign Out
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Box - Always render */}
      <UserBoxButton
        color="secondary"
        ref={ref}
        onClick={handleOpen}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={isOpen ? 'account-menu' : undefined}
      >
        <Avatar variant="rounded" alt={displayFirstName || 'User'} src={imgSrc} sx={avatarSx(40, '8px')}>
          {initial}
        </Avatar>
        <Hidden mdDown>
          <UserBoxText>
            <UserBoxLabel variant="body1">{hasUserData ? `${displayFirstName} ${displayLastName}` : 'Menu'}</UserBoxLabel>
            {displayPartyType && <UserBoxDescription variant="body2">{displayPartyType}</UserBoxDescription>}
          </UserBoxText>
        </Hidden>
        <Hidden smDown>
          <ExpandMoreTwoToneIcon sx={{ ml: 1, transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'none' }} />
        </Hidden>
      </UserBoxButton>

      <Popover
        anchorEl={ref.current}
        open={isOpen}
        onClose={handleClose} // outside click and Escape
        sx={{ backdropFilter: 'blur(0px)' }} // the theme blurs modal backdrops; keep the page readable behind the menu
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: 320,
              maxWidth: 'calc(100vw - 24px)',
              borderRadius: '10px',
              border: `1px solid ${alpha(theme.palette.common.black, 0.08)}`,
              boxShadow: theme.shadows[8],
              overflow: 'hidden'
            }
          }
        }}
      >
        {/* identity: name once, role once (as the badge), email */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2.5 }}>
          <Avatar alt={displayFirstName || 'User'} src={imgSrc} sx={avatarSx(56, '50%')}>
            {initial}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h4" noWrap sx={{ fontWeight: 700 }}>
              {hasUserData ? `${displayFirstName} ${displayLastName}` : displayFullName}
            </Typography>
            {user?.email && (
              <Typography variant="body2" color="text.secondary" noWrap>
                {user.email}
              </Typography>
            )}
            {displayPartyType && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mt: 0.75,
                  px: 1,
                  py: 0.25,
                  borderRadius: '999px',
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: 'primary.main',
                  fontSize: 12,
                  fontWeight: 600
                }}
              >
                <ShieldOutlinedIcon sx={{ fontSize: 14 }} />
                {displayPartyType}
              </Box>
            )}
          </Box>
        </Box>
        <Divider />
        <MenuList id="account-menu" autoFocusItem={isOpen} aria-label="Account menu" sx={{ p: 1 }}>
          <MenuItem sx={menuItemSx} selected={pathname === '/my-profile'} onClick={() => goTo('/my-profile')}>
            <IconTile tint={theme.colors.pastel.lilac}>
              <PersonOutlineRoundedIcon fontSize="small" />
            </IconTile>
            <Typography sx={{ flex: 1, fontWeight: 600 }}>My Profile</Typography>
            <ChevronRightRoundedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          </MenuItem>
          <MenuItem sx={menuItemSx} onClick={() => goTo('/my-profile#change-password')}>
            <IconTile tint={theme.colors.pastel.powder}>
              <LockOutlinedIcon fontSize="small" />
            </IconTile>
            <Typography sx={{ flex: 1, fontWeight: 600 }}>Change Password</Typography>
            <ChevronRightRoundedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          </MenuItem>
          <Divider sx={{ my: 0.75 }} />
          <MenuItem
            sx={{ ...menuItemSx, '&:hover, &.Mui-focusVisible': { bgcolor: alpha(theme.palette.error.main, 0.08) } }}
            onClick={() => {
              handleClose();
              handleLogout();
            }}
          >
            <IconTile tint={theme.colors.pastel.blush}>
              <LogoutOutlinedIcon fontSize="small" sx={{ color: 'error.main' }} />
            </IconTile>
            <Typography sx={{ flex: 1, fontWeight: 600, color: 'error.main' }}>Sign out</Typography>
          </MenuItem>
        </MenuList>
      </Popover>
    </>
  );
}

export default HeaderUserbox;
